import { DateTime, formatToSQL, getDateTime } from 'infras/datetime';
import { Dispatch, ReactNode, useCallback, useEffect, useRef } from 'react';
import {
  CalendarMode,
  ErrorCode,
  Errors,
  Schedule,
  SelectedGroup,
  SelectedSlot,
  ShowRange,
  Slot,
  SlotViewType,
  CalendarWeekDay,
  horizontalRulerWidth,
} from '.';
import {
  Action,
  setAllDayTimeGridRef,
  setCalendarMode,
  setContainerRef,
  setContainerWidth,
  setDayTimeGridRef,
  setDuration,
  setErrors,
  setIsShowEventOverlap,
  setIsInit,
  setIsReady,
  setIsShowSameEventMerge,
  setNow,
  setRulerStep,
  setRulerTimeZones,
  setSchedules,
  setSelectedGroups,
  setShowRange,
  setSlotViewType,
  setStartedAt,
  setStartWeekday,
  setTimeInterval,
  setTimes,
  setTimeZone,
  setUserColor,
  setWeekdayHeaderRef,
  State,
  setIsShowDeclinedEvent,
  setTimeFormat,
  setIsDisableMultiTimeZones,
  setRulerWidth,
  setPrimaryColor,
  setRulerMinHour,
  setRulerMaxHour,
  setHiddenWeekdays,
} from './CalendarContext';

export interface ProviderProps {
  cachePrefix?: string;
  timeZone?: string;
  interval?: number;
  rulerStep?: number;
  rulerBorderStep?: number;
  duration?: number;
  calendarMode: CalendarMode;
  slotViewType: SlotViewType;
  schedules?: Schedule[];
  showRange?: ShowRange;
  children?: ReactNode;
  startWeekday?: CalendarWeekDay;
  maxSlotsCount?: number;
  maxSlotGroupsCount?: number;
  rulerTimeZones?: string[];
  rulerMinHour?: number;
  rulerMaxHour?: number;
  isShowSameEventMerge?: boolean;
  isShowEventOverlap?: boolean;
  isShowDeclinedEvent?: boolean;
  hiddenWeekdays?: CalendarWeekDay[];
  timeFormat?: string;
  primaryColor?: string;
  userColor?: string;
  currentDate?: string;
  isDisableMultiTimeZones?: boolean;
  isAutoScrollOnInit?: boolean;
  isDisableHorizontalScroll?: boolean;
  onError?: (errors: Errors[]) => void;
  onUpdateSlotGroups?: (slots: Slot[]) => void;
  onUpdateSlots?: (slots: Slot[]) => void;
  onChangePage?: (page: number, startedAt: DateTime, endedAt: DateTime) => void;
}

export function useCalendarProvider(
  props: ProviderProps,
  { state, dispatch }: { state: State; dispatch: Dispatch<Action> },
) {
  const dayTimeGridRef = useRef(null);
  const allDayTimeGridRef = useRef(null);
  const weekdayHeaderRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!state.isDisableMultiTimeZones) return;
    dispatch(setRulerWidth(horizontalRulerWidth));
  }, [state.isDisableMultiTimeZones]);

  useEffect(() => {
    dispatch(setContainerRef(containerRef));
    dispatch(setDayTimeGridRef(dayTimeGridRef));
    dispatch(setAllDayTimeGridRef(allDayTimeGridRef));
    dispatch(setWeekdayHeaderRef(weekdayHeaderRef));
  }, []);

  // Provider Props
  useEffect(() => {
    if (!props.timeZone) return;
    dispatch(setTimeZone(props.timeZone));
  }, [props.timeZone]);

  useEffect(() => {
    if (!props.schedules) return;
    dispatch(setSchedules(props.schedules));
  }, [props.schedules]);

  useEffect(() => {
    if (!props.primaryColor) return;
    dispatch(setPrimaryColor(props.primaryColor));
  }, [props.primaryColor]);

  useEffect(() => {
    if (!props.userColor) return;
    dispatch(setUserColor(props.userColor));
  }, [props.userColor]);

  useEffect(() => {
    if (!props.duration) return;
    dispatch(setDuration(props.duration));
  }, [props.duration]);

  useEffect(() => {
    if (!props.rulerTimeZones) return;
    dispatch(setRulerTimeZones(props.rulerTimeZones));
  }, [props.rulerTimeZones]);

  useEffect(() => {
    if (!props.interval) return;
    dispatch(setTimeInterval(props.interval));
  }, [props.interval]);

  useEffect(() => {
    if (!props.calendarMode) return;
    dispatch(setCalendarMode(props.calendarMode));
  }, [props.calendarMode]);

  useEffect(() => {
    if (!props.slotViewType) return;
    dispatch(setSlotViewType(props.slotViewType));
  }, [props.slotViewType]);

  useEffect(() => {
    if (!props.rulerStep) return;
    dispatch(setRulerStep(props.rulerStep));
  }, [props.rulerStep]);

  useEffect(() => {
    if (props.rulerMinHour === undefined) return;
    dispatch(setRulerMinHour(props.rulerMinHour));
  }, [props.rulerMinHour]);

  useEffect(() => {
    if (props.rulerMaxHour === undefined) return;
    dispatch(setRulerMaxHour(props.rulerMaxHour));
  }, [props.rulerMaxHour]);

  useEffect(() => {
    if (props.showRange && props.currentDate) {
      dispatch(
        setShowRange(
          props.showRange,
          getDateTime(props.currentDate, state.timeZone),
        ),
      );
    } else if (props.showRange && !props.currentDate) {
      // dispatch(setShowRange(props.showRange));
    } else if (props.currentDate) {
      dispatch(
        setShowRange(
          state.showRange,
          getDateTime(props.currentDate, state.timeZone),
        ),
      );
    }
  }, [props.showRange, props.currentDate, state.timeZone]);

  useEffect(() => {
    if (props.isShowSameEventMerge === undefined) return;
    dispatch(setIsShowSameEventMerge(props.isShowSameEventMerge));
  }, [props.isShowSameEventMerge]);

  useEffect(() => {
    if (props.isShowEventOverlap === undefined) return;
    dispatch(setIsShowEventOverlap(props.isShowEventOverlap));
  }, [props.isShowEventOverlap]);

  useEffect(() => {
    if (props.hiddenWeekdays === undefined) return;
    dispatch(setHiddenWeekdays(props.hiddenWeekdays));
  }, [props.hiddenWeekdays]);

  useEffect(() => {
    if (props.isShowDeclinedEvent === undefined) return;
    dispatch(setIsShowDeclinedEvent(props.isShowDeclinedEvent));
  }, [props.isShowDeclinedEvent]);

  useEffect(() => {
    if (!props.timeFormat) return;
    dispatch(setTimeFormat(props.timeFormat));
  }, [props.timeFormat]);

  useEffect(() => {
    if (!props.startWeekday) return;
    dispatch(setStartWeekday(props.startWeekday));
  }, [props.startWeekday]);

  useEffect(() => {
    if (props.isDisableMultiTimeZones === undefined) return;
    dispatch(setIsDisableMultiTimeZones(props.isDisableMultiTimeZones));
  }, [props.isDisableMultiTimeZones]);

  // Error handle
  useEffect(() => {
    if (state.errors.length === 0) return;
    if (!props.onError || typeof props.onError !== 'function') return;
    props.onError(state.errors);
  }, [state.errors]);

  // TimeZone
  useEffect(() => {
    dispatch(setNow(getNow(state.timeZone)));
    const startedAt = state.startedAt.setZone(state.timeZone);
    dispatch(setStartedAt(startedAt));

    const timer = setInterval(() => {
      dispatch(setNow(getNow(state.timeZone)));
    }, 1000 * 60);
    return function cleanUp() {
      clearInterval(timer);
    };
  }, [state.timeZone]);

  // Slots
  useEffect(() => {
    const slotGroups = state.selectedGroups.map((group) => {
      return {
        startedAt: group.startedAt,
        endedAt: group.startedAt.plus({
          minute: group.ySpan * state.rulerStep,
        }),
      };
    });
    if (
      props.onUpdateSlotGroups &&
      typeof props.onUpdateSlotGroups === 'function'
    ) {
      props.onUpdateSlotGroups(slotGroups);
    }
  }, [state.selectedGroups]);

  useEffect(() => {
    const slots = state.selectedSlots.map((slot) => {
      return {
        startedAt: slot.date,
        endedAt: slot.date.plus({ minute: slot.ySpan * state.rulerStep }),
      };
    });
    if (props.onUpdateSlots && typeof props.onUpdateSlots === 'function') {
      props.onUpdateSlots(slots);
    }
  }, [state.selectedSlots]);

  // Layouts
  const resizeToFit = useCallback(() => {
    if (!state.containerRef?.current) return;
    const el: any = state.containerRef.current;
    if (!el?.clientWidth) return;
    dispatch(setContainerWidth(`${el.clientWidth}px`));
  }, [state.containerRef]);

  useEffect(() => {
    window.addEventListener('resize', resizeToFit);
    return function cleanUp() {
      window.removeEventListener('resize', resizeToFit);
    };
  }, [resizeToFit]);

  useEffect(() => {
    const times = getTimes({
      startedAt: state.startedAt,
      maxRow: state.maxRow,
      rulerStep: state.rulerStep,
      rulerMinHour: state.rulerMinHour,
      rulerMaxHour: state.rulerMaxHour,
    });
    dispatch(setTimes(times));
  }, [
    state.startedAt,
    state.rulerStep,
    state.rulerMinHour,
    state.rulerMaxHour,
    state.maxRow,
  ]);

  useEffect(() => {
    const newGroups: SelectedGroup[] = getGroupsFromSlots(
      state.selectedSlots,
      state.rulerStep,
    );
    if (
      typeof state.maxSlotGroupsCount !== 'undefined' &&
      newGroups.length > state.maxSlotGroupsCount
    ) {
      dispatch(
        setErrors([
          {
            code: ErrorCode.MAX_NUMBER_OF_SLOTS_EXCEEDED,
            message: 'The maximum number of slots has been exceeded',
          },
        ]),
      );
      return;
    }
    dispatch(setSelectedGroups(newGroups));
  }, [state.selectedSlots, state.rulerStep, state.maxSlotGroupsCount]);

  useEffect(() => {
    if (!state.containerRef?.current) return;
    const el: any = state.containerRef.current;
    if (!el?.clientWidth) return;
    dispatch(setContainerWidth(`${el.clientWidth}px`));
    dispatch(setIsInit(true));
  }, [state.containerRef?.current]);

  useEffect(() => {
    if (!state.containerRef) return;
    if (!state.containerWidth) return;
    if (state.isReady) return;
    dispatch(setIsReady(true));
  }, [state.containerWidth]);

  useEffect(() => {
    if (!state.isReady) return;
    if (props.onChangePage && typeof props.onChangePage === 'function') {
      const startedAt = state.pages[state.currentPage];
      const endedAt = startedAt.plus(state.showRange);
      props.onChangePage(state.currentPage, startedAt, endedAt);
    }
  }, [
    props.onChangePage,
    state.currentPage,
    state.pages,
    state.showRange,
    state.isReady,
  ]);
}

export const getGroupsFromSlots = (
  selectedSlots: SelectedSlot[],
  rulerStep: number,
) => {
  const newGroups: SelectedGroup[] = [];
  let group: SelectedGroup | null = null;
  for (const slot of selectedSlots) {
    // slotのySpanを考慮してgroupを生成
    if (group) {
      const groupEndY = group.y + group.ySpan;
      if (group.x !== slot.x || (groupEndY !== slot.y && groupEndY < slot.y)) {
        group.endedAt = group.startedAt.plus({
          minutes: rulerStep * group.ySpan,
        });
        newGroups.push(group);
        group = null;
      }
    }

    if (!group) {
      group = {
        x: slot.x,
        xSpan: 1,
        y: slot.y,
        ySpan: slot.ySpan,
        startedAt: slot.date,
        endedAt: slot.date,
        isAllDay: slot.isAllDay,
      };
    } else {
      const slotEndY = slot.y + slot.ySpan;
      group.ySpan = slotEndY - group.y;
    }
  }
  if (group) {
    if (group.isAllDay) {
      group.endedAt = group.startedAt
        .plus({
          day: group.xSpan,
        })
        .startOf('day');
    } else {
      group.endedAt = group.startedAt.plus({
        minutes: rulerStep * group.ySpan,
      });
    }
    newGroups.push(group);
  }
  return newGroups;
};

export const getNow = (timeZone: string) => {
  return getDateTime(null, timeZone).setZone(timeZone);
};

export const getCurrentStartedAt = (props: {
  current: DateTime;
  startWeekday: CalendarWeekDay;
  showRange: ShowRange;
}) => {
  let startedAt;
  if (props.showRange.month) {
    startedAt = props.current.startOf('month');
  } else if (props.showRange.day === 7) {
    const weekday = props.startWeekday;
    startedAt = props.current.set({ weekday }).startOf('day');
  } else {
    startedAt = props.current.startOf('day');
  }
  return startedAt;
};

export const getRulerStepFromDuration = (duration: number) => {
  if (duration >= 15) {
    if (duration % 15 === 0) {
      return 15;
    } else if (duration % 10 === 0) {
      return 10;
    } else if (duration % 5 === 0) {
      return 5;
    }
  }
  return duration;
};

export const getTimes = (props: {
  startedAt: DateTime;
  maxRow: number;
  rulerStep: number;
  rulerMaxHour: number;
  rulerMinHour: number;
  timeZone?: string;
}) => {
  const denominator = 60 / props.rulerStep;
  return Array.from({ length: props.maxRow }).map((v, i) => {
    const hour = Math.floor(i / denominator) + props.rulerMinHour;
    const minute = (i % denominator) * props.rulerStep;
    if (props.timeZone) {
      return props.startedAt.set({ hour, minute }).setZone(props.timeZone);
    } else {
      return props.startedAt.set({ hour, minute });
    }
  });
};

export const getDefaultPages = (startedAt: DateTime, showRange: ShowRange) => {
  let pages = [
    startedAt.minus(showRange),
    startedAt,
    startedAt.plus(showRange),
  ];
  if (showRange.day) {
    pages = [
      ...pages,
      startedAt.plus({ day: showRange.day * 2 }),
      startedAt.plus({ day: showRange.day * 3 }),
    ];
  } else if (showRange.month) {
    pages = [
      ...pages,
      startedAt.plus({ month: showRange.month * 2 }),
      startedAt.plus({ month: showRange.month * 3 }),
    ];
  }
  return pages;
};

export const getCurrentScrollTop = (currentPage: number) => {
  const els = document.getElementsByClassName('js-vertical-scroll-item');
  let top = 0;
  for (let index = 0; index < els.length; index++) {
    if (currentPage === index) break;
    top += els[index].clientHeight;
  }
  return top;
};

export const scrollToNow = (timeZone: string) => {
  let hour = getDateTime(null, timeZone).startOf('hour').hour - 1;
  if (hour < 0) hour = 0;
  const targets = document.getElementsByClassName(`js-ruler-${hour}`);
  if (targets.length > 0) {
    targets[0].scrollIntoView(true);
  }
};

export const sortSchedules = (schedules: Schedule[], timeZone: string) => {
  const offset = getDateTime().setZone(timeZone).offset * 60 * 1000;
  return schedules.sort((a, b) => {
    let as = getDateTime(a.startedAt).toMillis();
    let ae = getDateTime(a.endedAt).toMillis();
    let bs = getDateTime(b.startedAt).toMillis();
    let be = getDateTime(b.endedAt).toMillis();
    if (a.isAllDay) {
      as -= offset;
      ae -= offset;
    }
    if (b.isAllDay) {
      bs -= offset;
      be -= offset;
    }
    if (as !== bs) return as - bs;
    if (ae !== be) return be - ae;
    return 0;
  });
};
